import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { informer } from '@/components/ui/Fenetres';
import { useCamions, useCreateVoyage, useMines, useUpdateVoyage } from '@/hooks/useTransport';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import { useOnlineStatus } from '@/offline/useOnlineStatus';
import { offlineDB } from '@/offline/offlineDB';
import type { Voyage } from '@/types';

interface Props {
  open:    boolean;
  onClose: () => void;
  voyage?: Voyage; // undefined = création
}

/**
 * Brief SG-RLT-CDC-MODULES-145 §1.2 — formulaire mobile-friendly de saisie voyage.
 * 4 sections : Identification · Voyage aller · Voyage retour · Observations.
 * Validation tonnage : warn >90% chargeMaxTonnes, bloque côté backend si dépassement.
 */
export const SaisieVoyageDialog = ({ open, onClose, voyage }: Props) => {
  const user = useAuthStore((s) => s.user);
  const filialeKey = useAuthStore((s) => s.filiale?.filialeKey);
  const online = useOnlineStatus();
  const isEdit = !!voyage;
  const create = useCreateVoyage();
  const update = useUpdateVoyage();
  const { data: camionsData } = useCamions({ actif: true, limit: 500 });
  const { data: minesData }   = useMines({ actif: true, limit: 100 });
  const camions = camionsData?.data ?? [];
  const mines   = minesData?.data ?? [];

  // Section 1 — Identification
  const [camionId, setCamionId]         = useState('');
  const [chauffeurNom, setChauffeurNom] = useState('');
  // Section 2 — Voyage aller
  const [mineId, setMineId]                     = useState('');
  const [portDestination, setPortDestination]   = useState('');
  const [heureDepartMine, setHeureDepartMine]   = useState('');
  const [tonnageCharge, setTonnageCharge]       = useState('');
  const [numeroBonPesee, setNumeroBonPesee]     = useState('');
  const [kmDepart, setKmDepart]                 = useState('');
  // Section 3 — Voyage retour
  const [heureArriveePort, setHeureArriveePort] = useState('');
  const [heureDepartPort, setHeureDepartPort]   = useState('');
  const [kmRetour, setKmRetour]                 = useState('');
  // Section 4 — Observations
  const [incident, setIncident]                 = useState(false);
  const [incidentType, setIncidentType]         = useState('mecanique');
  const [incidentDesc, setIncidentDesc]         = useState('');
  const [notes, setNotes]                       = useState('');

  const [error, setError]   = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Reset à chaque ouverture
  useEffect(() => {
    if (!open) return;
    if (voyage) {
      setCamionId(voyage.camionId);
      setChauffeurNom(voyage.chauffeurNom);
      setMineId(voyage.mineId);
      setPortDestination(voyage.portDestination);
      setHeureDepartMine(voyage.heureDepartMine.slice(0, 16));
      setTonnageCharge(voyage.tonnageCharge);
      setNumeroBonPesee(voyage.numeroBonPesee);
      setKmDepart(voyage.kmDepart?.toString() ?? '');
      setHeureArriveePort(voyage.heureArriveePort?.slice(0, 16) ?? '');
      setHeureDepartPort(voyage.heureDepartPort?.slice(0, 16) ?? '');
      setKmRetour(voyage.kmRetour?.toString() ?? '');
      setIncident(!!voyage.incidentType);
      setIncidentType(voyage.incidentType ?? 'mecanique');
      setIncidentDesc(voyage.incidentDescription ?? '');
      setNotes(voyage.notes ?? '');
    } else {
      setCamionId(''); setChauffeurNom('');
      setMineId(''); setPortDestination('');
      setHeureDepartMine(toLocalDatetime(new Date()));
      setTonnageCharge(''); setNumeroBonPesee(''); setKmDepart('');
      setHeureArriveePort(''); setHeureDepartPort(''); setKmRetour('');
      setIncident(false); setIncidentType('mecanique'); setIncidentDesc(''); setNotes('');
    }
    setError(null); setWarning(null);
  }, [open, voyage]);

  // Camion sélectionné → auto-fill chauffeur + port + warn tonnage
  const camion = useMemo(() => camions.find((c) => c.id === camionId), [camions, camionId]);
  useEffect(() => {
    if (!camion || isEdit) return;
    if (!chauffeurNom && camion.chauffeurNom) setChauffeurNom(camion.chauffeurNom);
  }, [camion, chauffeurNom, isEdit]);

  const mine = useMemo(() => mines.find((m) => m.id === mineId), [mines, mineId]);
  useEffect(() => {
    if (!mine || isEdit) return;
    if (!portDestination && mine.portDestinationDefault) setPortDestination(mine.portDestinationDefault);
  }, [mine, portDestination, isEdit]);

  // Validation tonnage temps réel
  useEffect(() => {
    setWarning(null);
    const t = parseFloat(tonnageCharge);
    if (!t || !camion) return;
    const capacite = parseFloat(camion.capaciteTonnes);
    const chargeMax = camion.chargeMaxTonnes ? parseFloat(camion.chargeMaxTonnes) : null;
    if (t > capacite) {
      setWarning(`🛑 Tonnage ${t}T > capacité physique (${capacite}T) — refusé`);
    } else if (chargeMax && t > chargeMax) {
      setWarning(`⚠️ Tonnage ${t}T dépasse la charge contractuelle (${chargeMax}T)`);
    } else if (chargeMax && t > chargeMax * 0.9) {
      setWarning(`ℹ️ Tonnage ${t}T à plus de 90% de la charge contractuelle (${chargeMax}T)`);
    }
  }, [tonnageCharge, camion]);

  const canSubmit = camionId && chauffeurNom.trim() && mineId && portDestination.trim() && heureDepartMine && tonnageCharge && numeroBonPesee.trim();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const payload = {
      camionId,
      chauffeurNom:        chauffeurNom.trim(),
      mineId,
      portDestination:     portDestination.trim(),
      heureDepartMine:     new Date(heureDepartMine).toISOString(),
      tonnageCharge:       parseFloat(tonnageCharge),
      numeroBonPesee:      numeroBonPesee.trim(),
      kmDepart:            kmDepart ? parseInt(kmDepart, 10) : undefined,
      heureArriveePort:    heureArriveePort ? new Date(heureArriveePort).toISOString() : undefined,
      heureDepartPort:     heureDepartPort  ? new Date(heureDepartPort).toISOString()  : undefined,
      kmRetour:            kmRetour ? parseInt(kmRetour, 10) : undefined,
      incidentType:        incident ? (incidentType as 'mecanique' | 'securite' | 'meteo' | 'piste' | 'surcharge' | 'autre') : undefined,
      incidentDescription: incident ? incidentDesc.trim() : undefined,
      statut:              incident ? ('incident' as const) : undefined,
      notes:               notes.trim() || undefined,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: voyage!.id, payload });
      } else if (online) {
        await create.mutateAsync(payload);
      } else {
        // OFFLINE — save dans IndexedDB pour sync au retour de connexion (brief §contraintes)
        if (!filialeKey) {
          throw new Error('Filiale non identifiée — impossible de sauvegarder hors ligne');
        }
        const mineCode   = mines.find((m) => m.id === mineId)?.code   ?? '?';
        const camionCode = camions.find((c) => c.id === camionId)?.code ?? '?';
        await offlineDB.voyages.add({
          payload,
          preview: {
            camionCode,
            mineCode,
            chauffeur:  chauffeurNom.trim(),
            tonnage:    parseFloat(tonnageCharge),
            dateHeure:  payload.heureDepartMine,
          },
          createdAt:  new Date().toISOString(),
          syncStatus: 'pending',
          filialeKey,
        });
        informer('📴 Hors ligne — voyage sauvegardé localement', 'Sera synchronisé au retour de connexion.');
      }
      onClose();
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Modifier voyage ${voyage!.numero}` : 'Saisir un voyage'}
      subtitle={isEdit ? 'Modification dans les 2h suivant la création (chef d\'équipe)' : `Chef d'équipe : ${user?.fullName ?? '—'}`}
      size="lg"
    >
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="inline size-3 mr-1" />{error}
          </div>
        )}

        <Section title="Identification">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Camion *">
              <select value={camionId} onChange={(e) => setCamionId(e.target.value)} required disabled={isEdit} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {camions
                  .filter((c) => c.statut !== 'hors_service')
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} · {c.modele ?? ''} {c.immatriculation ?? ''} · cap. {c.capaciteTonnes}T{c.chargeMaxTonnes ? ` (max ${c.chargeMaxTonnes}T)` : ''}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Chauffeur *">
              <input value={chauffeurNom} onChange={(e) => setChauffeurNom(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="Nom du chauffeur" />
            </Field>
          </div>
        </Section>

        <Section title="Voyage aller">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mine de chargement *">
              <select value={mineId} onChange={(e) => setMineId(e.target.value)} required disabled={isEdit} className="ds-fi" style={{ width: '100%' }}>
                <option value="">— Sélectionner —</option>
                {mines.map((m) => (
                  <option key={m.id} value={m.id}>{m.code} · {m.nom}{m.exploitant ? ` (${m.exploitant})` : ''}</option>
                ))}
              </select>
            </Field>
            <Field label="Port de destination *">
              <input list="ports" value={portDestination} onChange={(e) => setPortDestination(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="Port Kamsar / Port Conakry" />
              <datalist id="ports">
                <option value="Port Kamsar" />
                <option value="Port Conakry" />
              </datalist>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Heure départ mine *">
              <input type="datetime-local" value={heureDepartMine} onChange={(e) => setHeureDepartMine(e.target.value)} required className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Tonnage chargé (T) *">
              <input type="number" inputMode="decimal" min={0} step="0.1" value={tonnageCharge} onChange={(e) => setTonnageCharge(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="52.5" />
            </Field>
            <Field label="N° bon de pesée *">
              <input value={numeroBonPesee} onChange={(e) => setNumeroBonPesee(e.target.value)} required className="ds-fi" style={{ width: '100%' }} placeholder="BP-2026-…" />
            </Field>
          </div>
          {warning && (
            <div style={{ fontSize: 11, color: warning.startsWith('🛑') ? '#dc2626' : warning.startsWith('⚠️') ? '#d97706' : 'var(--tx-3)', marginTop: 4 }}>{warning}</div>
          )}
          <Field label="Km compteur départ (optionnel)">
            <input type="number" inputMode="numeric" value={kmDepart} onChange={(e) => setKmDepart(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
          </Field>
        </Section>

        <Section title="Voyage retour (optionnel — saisissable plus tard)">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Heure arrivée port">
              <input type="datetime-local" value={heureArriveePort} onChange={(e) => setHeureArriveePort(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Heure départ port">
              <input type="datetime-local" value={heureDepartPort} onChange={(e) => setHeureDepartPort(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
            <Field label="Km compteur retour">
              <input type="number" inputMode="numeric" value={kmRetour} onChange={(e) => setKmRetour(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
            </Field>
          </div>
          {kmDepart && kmRetour && (
            <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>
              Distance parcourue : <strong style={{ color: 'var(--p)' }}>{Math.max(0, parseInt(kmRetour, 10) - parseInt(kmDepart, 10))} km</strong>
            </div>
          )}
        </Section>

        <Section title="Observations">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={incident} onChange={(e) => setIncident(e.target.checked)} />
            Signaler un incident pendant le voyage
          </label>
          {incident && (
            <div className="grid grid-cols-2 gap-3" style={{ marginTop: 8 }}>
              <Field label="Type d'incident *">
                <select value={incidentType} onChange={(e) => setIncidentType(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
                  <option value="mecanique">Mécanique</option>
                  <option value="securite">Sécurité</option>
                  <option value="meteo">Météo</option>
                  <option value="piste">Piste dégradée</option>
                  <option value="surcharge">Surcharge</option>
                  <option value="autre">Autre</option>
                </select>
              </Field>
              <Field label="Description">
                <input value={incidentDesc} onChange={(e) => setIncidentDesc(e.target.value)} className="ds-fi" style={{ width: '100%' }} />
              </Field>
            </div>
          )}
          <Field label="Notes libres">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="ds-fi" rows={2} style={{ width: '100%' }} />
          </Field>
        </Section>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {create.isPending || update.isPending ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Enregistrer le voyage'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <fieldset style={{ border: '1px solid var(--bdr)', borderRadius: 'var(--r-sm)', padding: 10 }} className="space-y-2">
    <legend style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-2)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 6px' }}>{title}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);

function toLocalDatetime(d: Date) {
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60_000);
  return local.toISOString().slice(0, 16);
}
